import { withStory, WithStoryProps } from '@storyofams/storyblok-toolkit';
import { GetStaticPaths, GetStaticProps } from 'next';
import SbEditable from 'storyblok-react';

import { Seo, Button, Heading, Container, Block } from '~/components';
import { BannerDynamic } from '~/components/Banners/BannerDynamic';
import { Faq } from '~/components/Blocks/Faq';
import { VacancyHeader } from '~/components/Careers';
import { enhancedStaticProps, sdk as sdkClient, withLayout } from '~/lib';
import { FooterComponent, NavigationComponent } from '../../graphql/sdk';

export interface VacancyPageProps extends WithStoryProps {
  footer: FooterComponent;
  navigation: NavigationComponent;
}

const VacancyPage = ({ story }: VacancyPageProps) => {
  return (
    <div>
      <Seo story={story} />

      {story?.content?.title && (
        <VacancyHeader
          title={story?.content?.title}
          short={story?.content?.short}
          keywords={story?.content?.keywords}
        />
      )}

      <Container>
        <div className="flex flex-col lg:flex-row md:px-10">
          {!!story?.content?.body?.[0]?.component &&
            story?.content?.body?.map((section, i) => {
              return (
                <Block
                  component={story?.content?.component}
                  sectionType={section?.component}
                  content={section}
                  first={i === 0}
                  key={section._uid}
                  title={story?.content?.seo?.title || story?.content?.title}
                  className="px-0 md:pr-2"
                />
              );
            })}

          <div className="lg:ml-24">
            {story?.content?.apply_box && (
              <div className="border rounded-lg p-8 w-full lg:w-[312px] lg:sticky lg:top-24">
                {story?.content?.apply_box[0]?.job_details?.map((detail) => (
                  <div className="mt-10 first:mt-0">
                    <Heading
                      as="h6"
                      variant="h6"
                      className="font-sans text-grey-500"
                    >
                      {detail?.description}
                    </Heading>
                    <Heading as="h5" variant="h5" className="mt-3">
                      {detail?.content}
                    </Heading>
                  </div>
                ))}
                <Button
                  variant="primary"
                  className="mt-6 w-full"
                  href={story?.content?.apply_box[0].button_link?.url}
                >
                  {story?.content?.apply_box[0]?.button_text}
                </Button>
              </div>
            )}
          </div>
        </div>

        {story?.content?.faq && (
          <Faq
            kicker={story?.content?.faq?.content?.faq_kicker}
            title={story?.content?.faq?.content?.faq_title}
            questions={story?.content?.faq?.content?.faq}
            className="md:mx-10 mt-24"
          />
        )}
      </Container>

      {story?.content?.banner?.content && (
        <SbEditable content={story?.content?.banner?.content}>
          <div className="max-w-6xl mx-auto px-4 md:px-14">
            <BannerDynamic content={story.content.banner.content} />
          </div>
          <div className="h-80 bg-yellow-50 relative bottom-40 z-0 -mb-40" />
        </SbEditable>
      )}
    </div>
  );
};

export default withLayout(withStory(VacancyPage));

export const getStaticProps: GetStaticProps = enhancedStaticProps(
  async ({ params: { slug }, sdk }) => {
    let story;

    let notFound = false;

    try {
      story = (await sdk.vacancyPageItem({ slug: `/careers/${slug}` }))
        .VacancypageItem;
    } catch (e) {
      notFound = true;
    }

    return {
      props: {
        story,
      },
      notFound: notFound || !story,
      revalidate: 60,
    };
  },
);

export const getStaticPaths: GetStaticPaths = async () => {
  let stories;

  try {
    stories = (await sdkClient.vacancyPageItems()).VacancypageItems.items;
  } catch (e) {
    console.log(e);
  }

  return {
    paths: stories?.map(({ slug }) => ({
      params: { slug },
    })),
    fallback: 'blocking',
  };
};
